import React, { useEffect, useState, useRef, useMemo } from 'react';
import { Button, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { projectFirestore } from '../../firebase';
import '../../styles/Search.css';

const defaultPic =
  'https://firebasestorage.googleapis.com/v0/b/auth-development-53223.appspot.com/o/profile%20picture.png?alt=media&token=6ebbbce8-3caf-4017-a8c3-0b126224e632';

const PAGE_SIZE = 10;

export default function Search() {
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const formValue = useRef(null);

  useEffect(() => {
    const getUsers = async () => {
      setLoading(true);
      try {
        const snapshot = await projectFirestore.collection('usernames').get();
        const list = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            img: doc.data().img,
          }))
          .sort((a, b) => a.id.localeCompare(b.id));
        setAllUsers(list);
      } catch (err) {
        console.error('Search load users:', err);
        setAllUsers([]);
      } finally {
        setLoading(false);
      }
    };
    getUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return allUsers;
    const term = search.trim().toLowerCase();
    return allUsers.filter((user) =>
      user.id.toLowerCase().includes(term)
    );
  }, [allUsers, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  const rangeStart = filteredUsers.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredUsers.length);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formValue.current) {
      setSearch(formValue.current.value.toLowerCase().trim());
    }
  };

  const handleSearchInput = (e) => {
    setSearch(e.target.value.toLowerCase());
  };

  return (
    <div className="search-page">
      <h1 className="search-title">Search Accounts</h1>

      <Form className="search-form" onSubmit={handleSubmit}>
        <InputGroup>
          <Form.Control
            ref={formValue}
            type="search"
            placeholder="Filter by username..."
            aria-label="search"
            onChange={handleSearchInput}
          />
          <Button type="submit">Search</Button>
        </InputGroup>
      </Form>

      {!loading && filteredUsers.length > 0 && (
        <p className="search-count">
          Showing {rangeStart}–{rangeEnd} of {filteredUsers.length} accounts
        </p>
      )}

      <ul className="search-results">
        {loading ? (
          <li className="search-empty">Loading accounts...</li>
        ) : paginatedUsers.length > 0 ? (
          paginatedUsers.map((user) => (
            <li key={user.id}>
              <div className="search-result-card">
                <img
                  className="search-result-avatar"
                  src={user.img || defaultPic}
                  alt=""
                />
                <Link className="search-result-link" to={`/user/${user.id}`}>
                  @{user.id}
                </Link>
              </div>
            </li>
          ))
        ) : (
          <li className="search-empty">
            {search ? 'No results found.' : 'No accounts yet.'}
          </li>
        )}
      </ul>

      {!loading && filteredUsers.length > PAGE_SIZE && (
        <div className="search-pagination">
          <Button
            type="button"
            className="search-page-btn"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="search-page-info">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            type="button"
            className="search-page-btn"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

